import React, { useRef, useEffect, useState } from 'react';
import { array, bool, number, object } from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { I18n } from '@fiverr-private/i18n-react';
import { useGigPageContext } from '@fiverr-private/gig_page_context';
import { getContext } from '@fiverr-private/fiverr_context';
import { getPackageById, getSelectedPackageId, isTriplePackage } from '../../../reducers/packages';
import { isVoiceOver } from '../../../utils/packagesUtils';
import PackageContent from '../../shared/PackageContent';
import Logger from '../../../utils/logger';
import { shouldShowSelectPackage } from '../../../utils/shouldShowSelectPackage';
import { ContactSeller } from '../../ContactSeller/ContactSeller';
import { isInLynxesCtaReworkTest } from '../../../utils/experiments/isInLynxesCtaRework';
import { StickyContactCTA } from '../../StickyContactCTA';
import { ELEMENT_INTERSECTION_RATIO } from '../../../utils/ImpressionObserver/constants';
import TabFooter from './TabFooter';
import PaymentDrawerFooter from './PaymentDrawerFooter';
import { shouldShowRequestToOrderBtn } from './TabFooter/RequestToOrderBtn/utils/shouldShowRequestToOrderBtn';

import classes from './style.module.scss';

const TabContent = (
    { id, currentPrice, recommendedGig, features, packageId, customOrder, packageList },
    { general, rollouts, gig: { instantOrderSettings } }
) => {
    const { isLimitedEdition } = general;
    const { seller } = useGigPageContext();
    const shouldShowHourlyIndications = seller?.hourlyRate;
    const { isMobile } = getContext();

    const isGigActive = shouldShowSelectPackage(general);
    const showRequestToOrder = shouldShowRequestToOrderBtn({ instantOrderSettings, packageId, rollouts });

    // TODO: kill TabFooter
    const showDrawer = isGigActive && !showRequestToOrder;

    // TODO: remove when VO package has a title
    const title = isVoiceOver(general) ? 'gig_page_perseus.packages.order_details' : '';

    const wrapperClass = classNames(classes.tabContent, { [classes.recommended]: recommendedGig });
    const isInLynxesCtaReworkExperiment = isInLynxesCtaReworkTest(packageList || []) && !showRequestToOrder;

    const contactSellerRef = useRef(null);
    const [showStickyCTA, setShowStickyCTA] = useState(false);

    useEffect(() => {
        if (!isInLynxesCtaReworkExperiment) {
            return;
        }

        if (!contactSellerRef.current) {
            return;
        }

        const element = contactSellerRef.current;

        const handleIntersection = (entries) => {
            entries.forEach((entry) => {
                const isVisible = entry.intersectionRatio >= ELEMENT_INTERSECTION_RATIO;
                setShowStickyCTA(!isVisible);
            });
        };

        const observer = new IntersectionObserver(handleIntersection, {
            threshold: ELEMENT_INTERSECTION_RATIO,
        });

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [isInLynxesCtaReworkExperiment]);

    return (
        <div className={wrapperClass}>
            <PackageContent
                id={id}
                className={classes.packageContent}
                showPrice={!isLimitedEdition}
                title={title ? <I18n k={title} /> : ''}
                features={features}
            />
            {isInLynxesCtaReworkExperiment && (
                <div ref={contactSellerRef}>
                    <ContactSeller customOrder={customOrder} isInLynxesCtaReworkExperiment={isInLynxesCtaReworkExperiment && !showRequestToOrder} />
                </div>
            )}
            {showDrawer ? <PaymentDrawerFooter isInLynxesCtaReworkExperiment={isInLynxesCtaReworkExperiment && !showRequestToOrder} /> : <TabFooter currentPrice={currentPrice} />}
            {!isInLynxesCtaReworkExperiment && shouldShowHourlyIndications && <ContactSeller customOrder={customOrder} />}
            {isInLynxesCtaReworkExperiment && isMobile && <StickyContactCTA customOrder={customOrder} hidden={!showStickyCTA} isInLynxesCtaReworkExperiment={isInLynxesCtaReworkExperiment} />}
        </div>
    );
};

TabContent.propTypes = {
    id: number,
    currentPrice: number,
    recommendedGig: bool,
    features: array,
    packageId: number,
    customOrder: object,
    packageList: array,
};

TabContent.contextTypes = {
    general: object,
    rollouts: object,
    gig: object,
};

const mapStateToProps = (state, ownProps) => {
    const { packages } = state;
    const packageData = getPackageById(packages, ownProps.id);
    const triplePackage = isTriplePackage(packages);

    if (!packageData.title) {
        const logger = Logger.getInstance();
        logger.warn({
            message: 'missing package title',
            is_triple_package: triplePackage,
        });
    }

    return {
        ...packageData,
        packageId: getSelectedPackageId(state),
        features: ownProps.features,
        packageList: packages?.packageList || [],
    };
};

export { TabContent };
export default connect(mapStateToProps)(TabContent);